import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import {
  FiPlus, FiSettings, FiArrowLeft, FiCopy, FiCheckCircle, FiXCircle,
} from 'react-icons/fi';

const API = '/api/ecommerce';

export default function EcommerceAdmin() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingVendor, setEditingVendor] = useState(null);
  const [editName, setEditName] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [copiedKey, setCopiedKey] = useState(null);
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem('adminApiKey') || '');

  const loadVendors = useCallback(async () => {
    if (!adminKey.trim()) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/vendors`, { headers: { 'X-Admin-API-Key': adminKey } });
      setVendors(res.data);
    } catch (e) {
      const err = e.response?.data?.error;
      toast.error(typeof err === 'string' ? err : err?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => { loadVendors(); }, [loadVendors]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const res = await axios.post(`${API}/vendors`, { name: newName.trim() }, { headers: { 'X-Admin-API-Key': adminKey } });
      toast.success(`Vendor "${newName.trim()}" created!`);
      setVendors([res.data, ...vendors]);
      setNewName('');
      setShowCreate(false);
    } catch (e) {
      const err = e.response?.data?.error;
      toast.error(typeof err === 'string' ? err : err?.message || e.message);
    }
  };

  const handleUpdate = async (vendorId) => {
    try {
      await axios.put(`${API}/vendors/${vendorId}`, {
        name: editName,
        isActive: editActive,
      }, { headers: { 'X-Admin-API-Key': adminKey } });
      toast.success('Vendor updated');
      setEditingVendor(null);
      loadVendors();
    } catch (e) {
      const err = e.response?.data?.error;
      toast.error(typeof err === 'string' ? err : err?.message || e.message);
    }
  };

  const copyApiKey = (key) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
    toast.success('API key copied to clipboard');
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="w-full max-w-4xl mx-auto px-6 py-8">
        {/* Admin key */}
        <div className="mb-6 bg-white rounded-xl border border-neutral-200 shadow-sm p-4">
          <label className="block text-sm font-medium text-neutral-700 mb-1.5">Admin API Key</label>
          <input
            type="password"
            value={adminKey}
            onChange={e => {
              const key = e.target.value;
              setAdminKey(key);
              sessionStorage.setItem('adminApiKey', key);
            }}
            placeholder="Enter X-Admin-API-Key"
            className="w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
          />
          <p className="text-xs text-neutral-500 mt-1.5">
            This key is checked against the server's <code className="font-mono text-neutral-400">ADMIN_API_KEY</code> environment variable.
          </p>
        </div>

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Super Admin</h1>
            <p className="text-sm text-neutral-500 mt-1">Manage vendors and their API keys</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/ecommerce"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-neutral-200 bg-white text-sm font-semibold text-neutral-700 hover:bg-neutral-50 transition-colors shadow-sm"
            >
              <FiArrowLeft className="w-4 h-4" />
              Vendor Portal
            </Link>
            <Link to="/"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-neutral-200 bg-white text-sm font-semibold text-neutral-700 hover:bg-neutral-50 transition-colors shadow-sm"
            >
              <FiArrowLeft className="w-4 h-4" />
              WhatsApp
            </Link>
          </div>
        </div>

        {/* Create vendor */}
        <div className="mb-6 bg-white rounded-xl border border-neutral-200 shadow-sm p-4">
          {showCreate ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Vendor name (e.g. Fashion Store)"
                className="flex-1 px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleCreate()}
              />
              <button onClick={handleCreate} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700">
                Create
              </button>
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-500 hover:bg-neutral-50">
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
            >
              <FiPlus className="w-4 h-4" />
              Create New Vendor
            </button>
          )}
        </div>

        {/* Vendor list */}
        {loading ? (
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-8 text-center text-neutral-400">
            Loading vendors...
          </div>
        ) : vendors.length === 0 ? (
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-8 text-center text-neutral-400">
            No vendors yet. Create one to get started.
          </div>
        ) : (
          <div className="space-y-3">
            {vendors.map(vendor => (
              <div key={vendor.id} className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4">
                {editingVendor === vendor.id ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        className="flex-1 px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      />
                      <label className="flex items-center gap-1 text-sm text-neutral-600">
                        <input
                          type="checkbox"
                          checked={editActive}
                          onChange={e => setEditActive(e.target.checked)}
                          className="rounded"
                        />
                        Active
                      </label>
                      <button onClick={() => handleUpdate(vendor.id)} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700">
                        Save
                      </button>
                      <button onClick={() => setEditingVendor(null)} className="px-3 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-500 hover:bg-neutral-50">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-neutral-900">{vendor.name}</h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          vendor.is_active
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {vendor.is_active ? (
                            <><FiCheckCircle className="w-3 h-3" /> Active</>
                          ) : (
                            <><FiXCircle className="w-3 h-3" /> Inactive</>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-neutral-500 font-mono bg-neutral-50 px-2 py-1 rounded">
                          {vendor.api_key}
                        </code>
                        <button
                          onClick={() => copyApiKey(vendor.api_key)}
                          className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                        >
                          {copiedKey === vendor.api_key ? <FiCheckCircle className="w-3 h-3" /> : <FiCopy className="w-3 h-3" />}
                          {copiedKey === vendor.api_key ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <p className="text-xs text-neutral-400 mt-1">
                        Created: {new Date(vendor.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setEditingVendor(vendor.id);
                          setEditName(vendor.name);
                          setEditActive(!!vendor.is_active);
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-neutral-200 text-sm text-neutral-600 hover:bg-neutral-50"
                      >
                        <FiSettings className="w-3.5 h-3.5" />
                        Edit
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Info box */}
        <div className="mt-6 bg-blue-50 rounded-xl border border-blue-200 p-4">
          <h4 className="text-sm font-bold text-blue-900 mb-2">How to use</h4>
          <ol className="space-y-1 text-sm text-blue-700 list-decimal list-inside">
            <li>Create a vendor above and copy their API key</li>
            <li>Share the API key with the vendor</li>
            <li>Vendor logs in at the <Link to="/ecommerce" className="underline font-medium">Vendor Portal</Link> using their API key</li>
            <li>Vendor can then add products, send WhatsApp campaigns, and manage orders</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
